import { useMemo } from 'react';
import { pick } from 'ramda';
import { TFormField } from 'js/components/common/Form/types';
import { TRegistrationInfo } from 'js/hooks/user/store/types';
import { useUserSelector, useAppSelector } from 'js/store/hooks';
import { FORM_FIELD_TYPE } from 'js/components/common/Form/constants';

import { imsToFieldValue } from '../../../helpers';
import { useDynamicReducerSelector } from '../../../store';

export const useRegistrationUserValues = () => {
    const { fields, isRegistration } = useDynamicReducerSelector(pick(['fields', 'isRegistration']));
    const { currencyCode, registrationInfo } = useUserSelector(pick(['currencyCode', 'registrationInfo']));
    const { defaultCurrency, allowedCurrencies, allowedCountries } = useAppSelector(pick([
        'defaultCurrency', 'allowedCurrencies', 'allowedCountries'
    ]));

    return useMemo((): Record<string, any> => {
        if (!isRegistration) {
            return {};
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { playerHash, ...details } = registrationInfo || {};
        const additionalData = { allowedCurrencies, allowedCountries };

        return fields.reduce((accumulator, field: TFormField) => {
            if (!field.imsField) {
                return accumulator;
            }

            if ('currency' in details && field.type === FORM_FIELD_TYPE.CURRENCY_CODE) {
                accumulator[field.name] = imsToFieldValue(
                    details.currency || currencyCode,
                    { ...field, defaultValue: defaultCurrency },
                    additionalData
                );
            } else if (field.imsField in details) {
                accumulator[field.name] = imsToFieldValue(
                    details[field.imsField as keyof Omit<TRegistrationInfo, 'playerHash'>],
                    field,
                    additionalData
                );
            }

            return accumulator;
        }, {} as Record<string, any>);
    }, [allowedCountries, allowedCurrencies, currencyCode, defaultCurrency, fields, isRegistration, registrationInfo]);
};
